import { useSyncExternalStore } from "react";
import { CONTACT } from "@/lib/constants";

/**
 * Booking runs on a Google Calendar appointment schedule. The link lives entirely in
 * NEXT_PUBLIC_BOOKING_URL, so the calendar can change per environment without a code change.
 *
 * - Unset: every CTA stays on the pre-filled email, never a dead button.
 * - A full schedule link (calendar.google.com/calendar/appointments/schedules/...): a plain
 *   click opens Google's own embed in a dialog over the page (BookingDialog).
 * - Any other link, such as the calendar.app.google short link: Google refuses to be framed
 *   there, so it opens in a new tab.
 *
 * The qualifying questions are set up on the Google booking form itself, so the answers
 * arrive in the calendar invite. BOOKING_INTRO tells visitors why they are worth answering.
 */
export const BOOKING_URL = CONTACT.BOOKING_URL;

export const BOOKING_ENABLED = Boolean(BOOKING_URL);

/** Google's embeddable form of the schedule page, or null when it cannot be framed. */
const toEmbed = (link: string) => {
  try {
    const url = new URL(link);
    if (url.protocol !== "https:" || url.hostname !== "calendar.google.com") return null;
    if (!url.pathname.includes("/appointments/")) return null;
    url.searchParams.set("gv", "true"); // the flag Google's own embed code carries
    return url.toString();
  } catch {
    return null;
  }
};

export const BOOKING_EMBED_URL = BOOKING_ENABLED ? toEmbed(BOOKING_URL) : null;

/**
 * What every CTA points at: the real booking page when there is one, so middle-clicks,
 * cmd-clicks and no-JS visitors still reach it; otherwise the pre-filled email.
 */
export const BOOKING_HREF = BOOKING_ENABLED ? BOOKING_URL : CONTACT.CONSULT_HREF;

/**
 * The pre-call questionnaire (a Google Form). Optional: the booking dialog links it so
 * visitors can answer before the call, and Google's schedule description links it too.
 */
export const QUESTIONNAIRE_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSejo2VpLnRIdCucTKewGLjP6K3VzDClFIxkNRHMhq_pirdnww/viewform";

/** The approved intro shown with the booking dialog, the closing section and the client card. */
export const BOOKING_INTRO = {
  lead: "Let's make the call count.",
  body: "Book a 30-minute slot with the team. Before we meet, answer a few quick questions, so we come prepared with ideas, examples and a rough plan for your project instead of spending the call on basics. It takes about 2 minutes.",
} as const;

// One dialog for the whole site, opened from any BookCall.
let open = false;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((listener) => listener());

export const openBooking = () => {
  if (!BOOKING_EMBED_URL || open) return;
  open = true;
  emit();
};

export const closeBooking = () => {
  if (!open) return;
  open = false;
  emit();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useBookingOpen = () =>
  useSyncExternalStore(
    subscribe,
    () => open,
    () => false,
  );
